import { DialogHeading } from '@ariakit/react';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import type { FetcherData } from 'types';

import ErrorIcon from '~/assets/icons/Error';
import TextInput from '~/components/Form/TextInput';
import SignUpMethodEvent from '~/events/SignUpMethodEvent';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { storeNotificationData } from '~/utils/browser';
import { validateEmail } from '~/utils/validation';

import { Button } from '../../../Button';
import ModalHeadings from '../ModalHeadings';
import { type ScreenProps, Stage } from '.';

function ErrorMessage({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation();

  return isActive ? (
    <div className="flex h-5 flex-row content-center items-center gap-1">
      <ErrorIcon />
      <p className="text-xs font-bold text-red-600">
        {t('login.emailAlreadyExistsError')}
      </p>
    </div>
  ) : (
    <></>
  );
}

function TnCBlurb() {
  const { code } = useRegion();

  switch (code) {
    case 'DE':
      return (
        <p className="text-grey-hide">
          Wenn sie fortfahren, stimmen sie unseren allgemeinen
          geschäftsbedingungen und datenschutzrichtlinien zu.
        </p>
      );
    default:
      return (
        <p className="text-xs leading-5 text-white">
          By continuing you agree to our{' '}
          <a href="/terms-and-conditions/" className="underline">
            terms &amp; conditions
          </a>{' '}
          and{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://app.privasee.io/privacy-portal/64ac2c059f43880013f2faf0"
            className=" underline"
          >
            privacy policy
          </a>
        </p>
      );
  }
}

type Props = ScreenProps & { setEmail: Dispatch<SetStateAction<string>> };

export default function SignInScreen({
  postAuthOptions,
  setStage,
  setEmail,
  modalType
}: Props) {
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();
  const { isBot } = useRootData();
  const { t } = useTranslation();

  const [emailValue, setEmailValue] = useState('');
  const isEmailValid = validateEmail(emailValue);

  const errors = fetcher.data?.errors;
  const success = fetcher.data?.success;
  const submittedEmail = fetcher.data?.email;
  const submitting = fetcher.state === 'submitting';

  useEffect(() => {
    if (!success) return;

    setEmail(submittedEmail!);
    setStage(Stage.Complete);

    if (postAuthOptions?.action === 'restock') {
      storeNotificationData(postAuthOptions);
    }
  });

  return (
    <>
      {modalType === 'notifications' ? (
        <NotificationHeading />
      ) : (
        <ModalHeadings>{t('login.stageSignUp.subHeading')}</ModalHeadings>
      )}

      <fetcher.Form
        action="/bolt/register/"
        className="flex size-full flex-col items-start gap-4 bg-black"
        method="post"
      >
        <input
          type="hidden"
          value={postAuthOptions?.productId}
          name="productId"
        />
        <input type="hidden" value={postAuthOptions?.action} name="action" />
        <input type="hidden" value={user.guestId} name="guestId" />{' '}
        <TextInput
          className="h-12 w-full rounded-md p-4"
          error={
            errors?.email ? 'This is an incorrect email format' : undefined
          }
          id="login-email"
          name="email"
          onChange={(e) => setEmailValue(e.currentTarget.value)}
          placeholder={t('login.emailAddress')}
          type="email"
          value={emailValue}
        />
        <TnCBlurb />
        <Button
          className="h-12 w-full"
          variant={isEmailValid ? 'green-human' : 'disabled'}
          onClick={() =>
            new SignUpMethodEvent({
              buttonName: 'SIGN_UP_EMAIL',
              isBot,
              pagePosition: 'Sign in modal'
            }).sendToGtm()
          }
        >
          {submitting ? t('login.sendingConfirmationEmail') : t('continue')}
        </Button>
        <ErrorMessage isActive={Boolean(errors?.conflict)} />
      </fetcher.Form>
    </>
  );
}

function NotificationHeading() {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex max-h-[220px] flex-col items-center gap-6 pb-6 pt-4 text-center">
        <DialogHeading className="flex flex-col content-center text-lg font-semibold text-white">
          {t('login.stepHeading', { step: 1 })}
        </DialogHeading>
        <h2 className="text-white">{t('login.notificationHeading')}</h2>
      </div>
    </>
  );
}
